import { supabase } from './supabaseClient'

export type StressCopingStyle = 'auto' | 'conserve_energy' | 'movement_first'
export type PredictiveFeedbackType = 'helpful' | 'not_helpful' | 'movement_first' | 'conserve_energy'

export async function updateStressCopingStyle(
  style: StressCopingStyle,
): Promise<{ error: string | null }> {
  const { data: session } = await supabase.auth.getSession()
  const userId = session.session?.user?.id
  if (!userId) return { error: 'Not signed in' }

  const { error } = await supabase.from('profiles').update({ stress_coping_style: style }).eq('id', userId)
  if (error) {
    return {
      error:
        error.message.includes('stress_coping_style')
          ? 'Profile not ready — run the predictive learning database migration (supabase db push).'
          : error.message,
    }
  }
  return { error: null }
}

async function logPredictiveFeedback(
  userId: string,
  signalId: string,
  feedbackType: PredictiveFeedbackType,
): Promise<void> {
  await supabase.from('predictive_signal_feedback').upsert(
    {
      user_id: userId,
      signal_id: signalId,
      feedback_type: feedbackType,
    },
    { onConflict: 'user_id,signal_id,feedback_type' },
  )
}

export async function submitPredictiveFeedback(
  signalId: string,
  feedbackType: PredictiveFeedbackType,
): Promise<{ error: string | null }> {
  const { data: session } = await supabase.auth.getSession()
  const userId = session.session?.user?.id
  if (!userId) return { error: 'Not signed in' }

  if (feedbackType === 'movement_first' || feedbackType === 'conserve_energy') {
    const profileResult = await updateStressCopingStyle(feedbackType)
    if (profileResult.error) return profileResult
    await logPredictiveFeedback(userId, signalId, feedbackType)
    return { error: null }
  }

  const { error: feedbackError } = await supabase.from('predictive_signal_feedback').upsert(
    {
      user_id: userId,
      signal_id: signalId,
      feedback_type: feedbackType,
    },
    { onConflict: 'user_id,signal_id,feedback_type' },
  )

  if (feedbackError) {
    return {
      error: feedbackError.message.includes('predictive_signal_feedback')
        ? 'Feedback log unavailable — run supabase db push to enable full learning.'
        : feedbackError.message,
    }
  }

  return { error: null }
}

export function applyCopingStyleToSignal<T extends {
  trigger_context: Record<string, unknown>
  suggested_routine_override: Record<string, unknown> | null
}>(signal: T, style: 'movement_first' | 'conserve_energy'): T {
  const maintain = style === 'movement_first'
  return {
    ...signal,
    trigger_context: {
      ...signal.trigger_context,
      intervention_mode: maintain ? 'maintain' : 'deload',
      effective_coping_style: style,
      learning_applied: true,
    },
    suggested_routine_override: maintain
      ? {
          session_type: 'moderate',
          volume_adjustment_pct: 0,
          intensity: 'moderate',
          recommended_activities: ['planned_session', 'moderate_strength', 'zone2_cardio', 'mobility'],
          avoid: ['maximal_effort', 'new_PRs', 'training_to_failure'],
          intervention_mode: 'maintain',
          coach_note:
            'Your history shows training before similar events works for you. Keep today\'s session — stay sub-max and skip PR attempts.',
        }
      : {
          session_type: 'light',
          volume_adjustment_pct: -30,
          intensity: 'low',
          recommended_activities: ['walking', 'light_yoga', 'breathing_exercises'],
          avoid: ['maximal_effort', 'new_PRs'],
          intervention_mode: 'deload',
          coach_note:
            'Save maximal effort for after the event. A short walk or breathing work is ideal today.',
        },
  }
}
